import { DOMAIN_ERROR_CODES, type DomainErrorCode } from '@tonnie/foundation';

/**
 * Machine error code → translated copy keys, for every accept surface — the web
 * accept page and review dialog, the mobile review sheet.
 *
 * Exists because the page used to fall back to `err.message` — the SERVER's
 * message, which is English-only and written for logs. Combined with a broken
 * error contract that replaced every 403 body with `{message:'Forbidden'}`, the
 * citizen was shown a card titled "Something went wrong" whose body was the
 * single word "Forbidden". One of them retried three times and deleted their
 * account.
 *
 * So there is no message passthrough any more, and an unknown code falls to
 * translated generic copy. The map is `satisfies`-constrained, and a companion
 * spec asserts every key exists in all five locales — a new invitation error
 * that reaches this page without copy fails a test rather than rendering a raw
 * string in the wrong language.
 */
export interface InviteErrorCopy {
  titleKey: string;
  bodyKey: string;
}

/** Every code the accept / decline / eligibility calls can surface. */
export const INVITE_ERROR_COPY = {
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_NOT_FOUND]: {
    titleKey: 'invitations.error.notFoundTitle',
    bodyKey: 'invitations.error.notFoundDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_EXPIRED]: {
    titleKey: 'invitations.error.expiredTitle',
    bodyKey: 'invitations.error.expiredDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_REVOKED]: {
    titleKey: 'invitations.error.revokedTitle',
    bodyKey: 'invitations.error.revokedDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_DECLINED]: {
    titleKey: 'invitations.error.declinedTitle',
    bodyKey: 'invitations.error.declinedDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_ALREADY_ACCEPTED]: {
    titleKey: 'invitations.error.alreadyAcceptedTitle',
    bodyKey: 'invitations.error.alreadyAcceptedDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_EMAIL_MISMATCH]: {
    titleKey: 'invitations.error.emailMismatchTitle',
    bodyKey: 'invitations.error.emailMismatchDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_EMAIL_UNVERIFIED]: {
    titleKey: 'invitations.error.emailUnverifiedTitle',
    bodyKey: 'invitations.error.emailUnverifiedDescription',
  },
  [DOMAIN_ERROR_CODES.TALENT_INVITATION_WRONG_PORTAL]: {
    titleKey: 'invitations.error.wrongPortalTitle',
    bodyKey: 'invitations.error.wrongPortalDescription',
  },
  // D2 — a company's request for more, answered by the wrong account, or on a
  // relationship that is no longer live.
  [DOMAIN_ERROR_CODES.SHARE_REQUEST_NOT_YOURS]: {
    titleKey: 'invitations.error.requestNotYoursTitle',
    bodyKey: 'invitations.error.requestNotYoursDescription',
  },
  [DOMAIN_ERROR_CODES.SHARE_REQUEST_RELATIONSHIP_CLOSED]: {
    titleKey: 'invitations.error.requestClosedTitle',
    bodyKey: 'invitations.error.requestClosedDescription',
  },
} satisfies Partial<Record<DomainErrorCode, InviteErrorCopy>>;

/** The copy for a code this map does not know — generic, translated, never the server's. */
export const INVITE_ERROR_FALLBACK_COPY: InviteErrorCopy = {
  titleKey: 'invitations.error.acceptFailedTitle',
  bodyKey: 'invitations.error.tryAgain',
};

/** Codes this page is expected to handle. Drives the exhaustiveness test. */
export const HANDLED_INVITE_ERROR_CODES = Object.keys(INVITE_ERROR_COPY);

/**
 * Copy for a code, or the generic fallback.
 *
 * Never returns a server message. An unrecognised code means "we do not know
 * what happened", and saying so in the citizen's own language beats leaking an
 * English string written for an operator.
 */
export function inviteErrorCopy(code: unknown): InviteErrorCopy {
  const entry =
    typeof code === 'string'
      ? (INVITE_ERROR_COPY as Record<string, InviteErrorCopy | undefined>)[code]
      : undefined;
  return entry ?? INVITE_ERROR_FALLBACK_COPY;
}

/**
 * The copy for a code, translated. The ONE place an invitation error's keys go
 * through `t()`: they come from `INVITE_ERROR_COPY` or `INVITE_ERROR_FALLBACK_COPY`,
 * whose every key the companion spec proves exists in all five locales, so both
 * accept surfaces render through here instead of each calling `t()` on a
 * looked-up key.
 */
export function translateInviteError(
  t: (key: string) => string,
  code: unknown
): { title: string; body: string } {
  const copy = inviteErrorCopy(code);
  return { title: t(copy.titleKey), body: t(copy.bodyKey) };
}
